/* eslint react-hooks/exhaustive-deps: "warn" */
import { useRef, type JSX } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Flag } from 'ui/atoms/Flag/Flag';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DefenceSecurityProvenance.module.scss';

type IconKey = 'registry' | 'document' | 'calendar' | 'clock' | 'link';

type Stage = {
  label: string;
  value: string;
  mono?: string;
  icon: IconKey;
  end?: boolean;
  href?: string;
};

const ICONS: Record<IconKey, JSX.Element> = {
  registry: <path d="M4 10h16M6 10v9M10 10v9M14 10v9M18 10v9M5 19h14M12 3l8 5H4z" />,
  document: (
    <>
      <path d="M7 3.5h6.5L18 8v12.5H7z" />
      <path d="M13.5 3.5V8H18" />
      <path d="M9.5 13h5M9.5 16.5h5" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5.5" width="16" height="15" rx="2" />
      <path d="M4 10h16M8 3v4M16 3v4" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2.5" />
    </>
  ),
  link: (
    <path d="M10.5 13.5a4 4 0 005.66 0l2-2a4 4 0 10-5.66-5.66l-1 1M13.5 10.5a4 4 0 00-5.66 0l-2 2a4 4 0 105.66 5.66l1-1" />
  ),
};

type Translate = (key: string) => string;

const getStages = (t: Translate): Stage[] => [
  {
    label: t('Registry'),
    value: 'Companies House, United Kingdom',
    icon: 'registry',
  },
  {
    label: t('Filing it was read from'),
    value: 'PSC01 · persons with significant control',
    icon: 'document',
  },
  {
    label: t('Filed by the company'),
    value: '11 March 2026',
    icon: 'calendar',
  },
  {
    label: t('Retrieved by us'),
    value: '28 July 2026',
    mono: '06:14:11 UTC',
    icon: 'clock',
  },
  {
    label: t('Open the source yourself'),
    value: '',
    mono: 'find-and-update.company-information.service.gov.uk',
    icon: 'link',
    end: true,
    href: 'https://find-and-update.company-information.service.gov.uk',
  },
];

export const DefenceSecurityProvenance = () => {
  const { t } = useInt();
  const stages = getStages(t);
  const spineRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(spineRef, { threshold: 0.15 });

  return (
    <Section background="#F7F9FD" className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={t('Everyone cites something. We cite the registry.')}
          description={t(
            'A licence decision, a notification or a refusal has to survive an audit years later. What matters is not that a field has a source, but where that source resolves to.',
          )}
          layout="split"
          align="left"
        />
      </div>

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <span className={styles.cardHeadLabel}>{t('Chain of custody · single field')}</span>
          <span className={styles.sample}>SAMPLE</span>
        </div>

        <div className={styles.cardBody}>
          <div className={styles.claim}>
            <span className={styles.claimLabel}>{t('The field')}</span>
            <div className={styles.claimValue}>Ultimate parent</div>
            <p className={styles.claimText}>
              {t(
                'One value on one supplier record. Everything to the right of it is what you can put in front of an auditor, a licensing officer or a regulator without asking us for anything.',
              )}
            </p>
          </div>

          <div className={cn(styles.spine, revealed && styles.spineOn)} ref={spineRef}>
            {stages.map((stage, i) => (
              <div
                className={cn(styles.stage, stage.end && styles.stageEnd)}
                style={{ transitionDelay: `${i * 90}ms` }}
                key={stage.label}
              >
                <span className={styles.stageIcon}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {ICONS[stage.icon]}
                  </svg>
                </span>

                <div className={styles.stageContent}>
                  <span className={styles.stageLabel}>{stage.label}</span>
                  {stage.value && (
                    <div className={styles.stageValue}>
                      {i === 0 && <Flag code="gb" className={styles.flag} />}
                      {stage.value}
                    </div>
                  )}
                  {stage.mono &&
                    (stage.href ? (
                      <a className={styles.stageLink} href={stage.href} target="_blank" rel="noopener noreferrer">
                        {stage.mono}
                        <svg
                          className={styles.linkArrow}
                          viewBox="0 0 14 14"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.7"
                        >
                          <path d="M4 10 10 4M5 4h5v5" />
                        </svg>
                      </a>
                    ) : (
                      <div className={styles.stageMono}>{stage.mono}</div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.cardFoot}>
          <span className={styles.footLabel}>{t('A derived provider returns')}</span>
          <span className={styles.footText}>
            {t('Vendor company file, an internal record identifier and a refresh cycle.')}{' '}
            <b>{t('The filing is not carried through')}</b>
            {t(', so re-checking the value means going back to the vendor rather than to the register.')}
          </span>
        </div>
      </div>
    </Section>
  );
};
